/**
 * @file ソート可能なUI要素の型定義と型ガード関数
 * GraphQLの型定義を基にして、ドラッグ&ドロップUIに必要な型を定義
 */

import type { AdsFilterState } from "@/app/[lang]/[workspaceId]/ads/constants";
import type { SearchAnalyticsSearchType } from "@/app/[lang]/[workspaceId]/sa/constants";
import type { SortableItem } from "./sortable-context";

// ============================================================================
// 基本型定義
// ============================================================================

/**
 * プロジェクトの種別
 */
type ProjectType = "sa" | "ua" | "iw" | "ads";

/**
 * ソート可能な基本インターフェース
 */
interface BaseSortableItem extends SortableItem {
  /** 一意識別子 */
  id: string;
  /** 表示名 */
  name: string;
  /** ソート可能フラグ（デフォルト: true） */
  sortable?: boolean;
}

/**
 * ソート可能アイテムの基本プロパティ
 */
interface BaseItemProperties {
  /** ワークスペースID */
  workspaceId: string;
  /** プロジェクトID */
  projectId: string;
  /** プロジェクトの種別 */
  projectType: ProjectType;
}

// ============================================================================
// SAプロジェクト型
// ============================================================================

/**
 * SAプロジェクト固有のソート可能ビューアイテム（サイドバー用）
 */
export interface SASidebarItem extends BaseSortableItem, BaseItemProperties {
  projectType: "sa";
  /** 最新データ日付 */
  latestDataDate: string;
  /** 「すべて」カードフラグ */
  isAllCard?: boolean;
}

/**
 * SAプロジェクト固有のソート可能ビューアイテム（ボード画面用）
 */
export interface SAViewItem extends SASidebarItem {
  /** データ日付設定（ボード画面でのみ使用） */
  dataDate: {
    type: string;
    dateRange: { start: string; end: string } | null;
  } | null;
  /** 保存ビューの検索タイプ（比率の分母クエリで使用） */
  searchTypes?: SearchAnalyticsSearchType[] | null;
  /** 追加メニュー要素（ボード画面でのみ使用） */
  menu?: React.ReactNode;
}

// ============================================================================
// UAプロジェクト型
// ============================================================================

/**
 * UAプロジェクト固有のソート可能ビューアイテム（サイドバー用）
 */
export interface UASidebarItem extends BaseSortableItem, BaseItemProperties {
  projectType: "ua";
  /** 最新データ日付 */
  latestDataDate?: string;
  /** 「すべて」カードフラグ */
  isAllCard?: boolean;
  /** Session Explorerフラグ */
  isSessionExplorer?: boolean;
}

/**
 * ADSプロジェクト固有のソート可能ビューアイテム（サイドバー用）
 */
export interface ADSSidebarItem extends BaseSortableItem, BaseItemProperties {
  projectType: "ads";
  latestDataDate?: string;
  isAllCard?: boolean;
}

/**
 * ADSプロジェクト固有のソート可能ビューアイテム（ボード画面用）
 */
export interface ADSViewItem extends ADSSidebarItem {
  lang: string;
  description?: string | null;
  filters?: Partial<AdsFilterState>;
  menu?: React.ReactNode;
}

/**
 * UAプロジェクト固有のソート可能ビューアイテム（ボード画面用）
 */
export interface UAViewItem extends UASidebarItem {
  /** データ日付設定（ボード画面でのみ使用） */
  dataDate?: {
    type: string;
    dateRange: { start: string; end: string } | null;
  } | null;
  /** 追加メニュー要素（ボード画面でのみ使用） */
  menu?: React.ReactNode;
}

// ============================================================================
// IWプロジェクト型
// ============================================================================

/**
 * IWプロジェクト固有のソート可能ワーカーアイテム
 */
export interface IndexWorkerItem extends BaseSortableItem, BaseItemProperties {
  projectType: "iw";
}

// ============================================================================
// 共用型
// ============================================================================

/**
 * すべてのソート可能アイテムの共用型（サイドバー用）
 */
export type AnySidebarItem =
  | SASidebarItem
  | UASidebarItem
  | ADSSidebarItem
  | IndexWorkerItem;

/**
 * 特殊ビューアイテムのID定数
 */
export const SPECIAL_VIEW_IDS = {
  ALL: "all-card",
  INITIAL_ANALYSIS: "initial-analysis",
  SETTINGS: "settings",
  SESSION_EXPLORER: "session-explorer",
} as const;

/**
 * ビュー順序更新の結果型
 */
export interface UpdateResult {
  success: boolean;
  error?: string;
}

/**
 * ビュー順序更新関数のシグネチャ
 */
export type UpdateViewOrderFunction = (
  actionId: string,
  viewIds: string[],
  projectId: string,
) => Promise<UpdateResult>;
